// ============================================================================
// LE FORMULAIRE — champ, étiquette, aide, erreur.
//
// SOURCE : 265 champs de la maquette du back-office, relevés un par un (2026-08-16).
//
// ⚠️ RÈGLE DE LECTURE DES COULEURS : les valeurs viennent du `:root` de la maquette, JAMAIS du repli
// littéral d'un `var(--x, #…)` écrit en ligne. Six de ces replis contredisent son propre `:root` —
// relever à l'œil dans le HTML aurait embarqué six couleurs fausses dans la charte.
// ============================================================================

/**
 * HAUTEURS DE CHAMP — six crans nommés d'après leur USAGE constaté.
 * ⚠️ 38 px n'existait dans AUCUNE rampe alors que c'est la hauteur la plus fréquente de la maquette
 * (83 champs). Exactement le motif qui a justifié les crans de bouton et de pastille : une valeur
 * dominante sans nom se recopie à la main, puis dérive.
 * ⚠️ LE RAYON SUIT LA TAILLE, et ce n'est pas décoratif : un rayon de 10 sur une boîte de 28 px
 * mange la moitié de sa hauteur en courbe.
 */
export const FIELD_SIZES = {
  cell: { height: 28, padH: 7, radius: 'xs', fontSize: 12 },
  inline: { height: 32, padH: 8, radius: 'xs', fontSize: 12.5 },
  filter: { height: 34, padH: 10, radius: 'ctrl', fontSize: 12.5 },
  compact: { height: 36, padH: 10, radius: 'ctrl', fontSize: 13 },
  /** LE défaut : modale, réglages. */
  field: { height: 38, padH: 12, radius: 'ctrl', fontSize: 13 },
  form: { height: 40, padH: 12, radius: 'ctrl', fontSize: 13.5 },
} as const;
export type FieldSize = keyof typeof FIELD_SIZES;

export const FIELD_SURFACE = {
  /**
   * ⚠️ `paper` et non `canvas` (ce que la charte portait) : 186 champs sur 265 sont posés sur papier,
   * AUCUN sur le fond de page. Un champ creusé dans le fond disparaît dès qu'on le pose dans une
   * carte — et une carte est déjà du papier.
   */
  bg: 'paper',
  border: 'ink-200',
  borderWidth: 1,
  ink: 'ink-900',
  placeholder: 'ink-400',
} as const;

export const FIELD_STATE = {
  hover: 'ink-300',
  /** Unanime dans la maquette : 23 déclarations de focus sur 23. */
  focus: 'teal-400',
  /**
   * ⚠️ ANNEAU DE FOCUS AJOUTÉ, absent des deux côtés (la maquette écrit `outline:none` partout).
   * Un changement de teinte sur 1 px de bordure n'est pas un indicateur de focus clavier : il ne
   * survit ni au daltonisme, ni à un champ déjà bordé de rose parce qu'il est en erreur.
   * `:focus-visible` seulement — à la souris, le dessin de la maquette reste intact.
   */
  ring: { color: 'teal-500', width: 2, offset: 2 },
  transitionMs: 130,
} as const;

/**
 * ÉTATS SIGNIFIANTS DE LA BORDURE — et surtout ce qui NE change pas.
 * ⚠️ Seule la bordure change. Le fond reste papier, le texte reste à l'encre normale, l'épaisseur
 * reste 1 px : un champ en erreur qui change aussi de fond devient illisible avec son propre texte.
 */
export const FIELD_TONE_BORDER = {
  error: 'rose',
  /** « Modifié, pas encore enregistré » — l'ambre n'est pas une erreur, c'est une attente. */
  pending: 'amber',
} as const;

/**
 * CHAMP NON MODIFIABLE — et AUCUN `disabled`.
 * ⚠️ RÈGLE DE LA MAQUETTE, tenue ici : « un champ non modifiable n'est pas un champ ». Zéro
 * `disabled`, zéro `readonly`, zéro curseur barré dans ses 23 000 lignes. On rend donc une VALEUR
 * habillée, jamais un champ inerte — qui prendrait le focus pour ne rien accepter.
 */
export const FIELD_READONLY = {
  computed: { bg: 'ink-50', border: 'ink-200', weight: 700 },
  /** Verrouillé : la bordure RECULE d'un cran, elle ne grise pas le texte. */
  locked: { bg: 'ink-50', border: 'ink-100', weight: 600, lockIcon: 12 },
} as const;

/** ÉTIQUETTE — deux crans, répartis par contexte, jamais mélangés dans un même écran. */
export const FIELD_LABEL = {
  form: { fontSize: 11.5, weight: 500, ink: 'ink-500', marginBottom: 5 },
  dense: { fontSize: 11, weight: 600, ink: 'ink-500', marginBottom: 5 },
  errorInk: 'rose-fg',
  /**
   * ⚠️ AUCUN ASTÉRISQUE dans toute la maquette, et aucun attribut `required`. L'obligation s'écrit
   * en toutes lettres, et seulement là où elle mord : un astérisque est une convention que personne
   * n'a jamais apprise ; une phrase se lit.
   */
  requiredSuffix: ' — obligatoire',
} as const;

/** AIDE et ERREUR — sous le champ, jamais en infobulle : une aide qu'il faut survoler n'aide pas. */
export const FIELD_HINT = {
  fontSize: 11.5, ink: 'ink-500', marginTop: 5, lineHeight: 1.5,
  errorInk: 'rose-fg', errorWeight: 600,
} as const;

/** GROUPE — un titre de section et l'air qui l'entoure. */
export const FIELD_GROUP = { gap: 14, titleSize: 12, titleWeight: 700, titleInk: 'ink-900', titleMarginBottom: 10 } as const;
